import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "./auth/AuthProvider";
import { SessionProvider, useOptionalSession } from "./auth/SessionProvider";
import { ApiError } from "./api/client";
import { Shell } from "./shell/Shell";
import { Overview } from "./screens/Overview";
import { InvitePage } from "./screens/Invite";
import { TenantPicker } from "./screens/TenantPicker";
import { InvoiceQueue } from "./screens/InvoiceQueue";
import { AuditLogPage } from "./screens/AuditLog";
import { InvoiceReview } from "./screens/InvoiceReview";
import { PurchaseOrderPage } from "./screens/PurchaseOrder";
import { ReportsPage } from "./screens/Reports";
import { SettingsPage } from "./screens/Settings";
import { StaffConsole } from "./screens/StaffConsole";
import { MissionControl } from "./screens/MissionControl";
import { HealthPage } from "./screens/Health";
import { SignUpPage } from "./screens/SignUp";
import { ResetPasswordPage } from "./screens/ResetPassword";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
      staleTime: 5_000,
    },
  },
});

/** Tenant routes need a tenant: staff, and people in several workspaces, pick one first; staff routes need the Staff role. */
function Gate({ kind, children }: { kind: "tenant" | "staff"; children: JSX.Element }) {
  const session = useOptionalSession();
  const location = useLocation();
  if (!session) return <Navigate to="/" replace />;
  if (kind === "staff") return session.isStaff ? children : <Navigate to="/" replace />;
  if (!session.tenant) return session.isStaff || session.workspaces.length > 1 ? <TenantPicker /> : <Navigate to="/" replace state={{ from: location }} />;
  return children;
}

/** The old Invoices page lives under Reports now; a bookmarked search still lands there. */
function ToReportsInvoices() {
  const location = useLocation();
  return <Navigate to={"/reports/invoices" + location.search} replace />;
}

function Routed() {
  const session = useOptionalSession();
  return (
    <Routes>
      <Route path="/invite/:token" element={<InvitePage />} />
      {!session ? (
        <Route path="*" element={<Navigate to="/" replace />} />
      ) : (
        <Route element={<Shell />}>
          <Route path="/" element={<Gate kind="tenant"><Overview /></Gate>} />
          <Route path="/queue" element={<Gate kind="tenant"><InvoiceQueue /></Gate>} />
          <Route path="/needs" element={<Navigate to="/queue?tab=needs" replace />} />
          <Route path="/invoices" element={<ToReportsInvoices />} />
          <Route path="/audit" element={<Gate kind="tenant"><AuditLogPage /></Gate>} />
          <Route path="/invoices/:id" element={<Gate kind="tenant"><InvoiceReview /></Gate>} />
          <Route path="/po/:job/:po" element={<Gate kind="tenant"><PurchaseOrderPage /></Gate>} />
          <Route path="/po/:job" element={<Gate kind="tenant"><PurchaseOrderPage /></Gate>} />
          <Route path="/reports/*" element={<Gate kind="tenant"><ReportsPage /></Gate>} />
          <Route path="/settings/*" element={<Gate kind="tenant"><SettingsPage /></Gate>} />
          <Route path="/staff/*" element={<Gate kind="staff"><StaffConsole /></Gate>} />
          <Route path="/ops/*" element={<Gate kind="staff"><MissionControl /></Gate>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      )}
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public pages need no sign-in. */}
          <Route path="/health" element={<HealthPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/*" element={<AuthProvider><SessionProvider><Routed /></SessionProvider></AuthProvider>} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
